import { useCallback, useState, useEffect } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ListItem } from '@frontend/app/refresh-components/DraggableList';
import type { SocialAccountsColumn } from '../social-accounts-column-dialog';

interface UseSocialAccountsColumnDialogConfig {
  selectedColumns: SocialAccountsColumn[];
  setSelectedColumns: React.Dispatch<React.SetStateAction<SocialAccountsColumn[]>>;
  onSave?: (columns: SocialAccountsColumn[]) => Promise<void>;
}

export const useSocialAccountsColumnDialog = ({
  selectedColumns,
  setSelectedColumns,
  onSave,
}: UseSocialAccountsColumnDialogConfig) => {
  const [editModeColumns, setEditModeColumns] = useState<SocialAccountsColumn[]>([]);

  useEffect(() => {
    setEditModeColumns(selectedColumns);
  }, [selectedColumns]);

  // Manage dialog open state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = useCallback(() => setIsDialogOpen(true), []);

  const saveColumns = useCallback(
    async (columns: SocialAccountsColumn[]) => {
      if (onSave) {
        await onSave(columns);
      }
      // Update the parent state with the updated columns (including new order and hidden state)
      setSelectedColumns(columns);
    },
    [onSave, setSelectedColumns],
  );

  const onToggleColumnCheckBox = useCallback((id: string) => {
    setEditModeColumns((prevColumns) =>
      prevColumns.map((col) => (col.id === id ? { ...col, hidden: !col.hidden } : col)),
    );
  }, []);

  const onToggleSelectall = useCallback((checked: CheckedState) => {
    setEditModeColumns((prevColumns) => prevColumns.map((col) => ({ ...col, hidden: !checked })));
  }, []);

  const handleDragEndModalColumn = useCallback(
    (items: ListItem<SocialAccountsColumn>[], _action?: 'removeItem' | 'dragItem') => {
      const updatedColumns = items.map((item, index) => ({
        ...item.data,
        order: index + 1,
      }));
      setEditModeColumns(updatedColumns);
    },
    [],
  );

  return {
    columnDialogProps: {
      selectedColumns: editModeColumns,
      setSelectedColumns: setEditModeColumns,
      handleDragEndModalColumn,
      onToggleColumnCheckBox,
      onSave: saveColumns,
      onToggleSelectall,
      isOpen: isDialogOpen,
      onOpenChange: setIsDialogOpen,
    },
    openDialog,
  };
};
